import { Policy, RuleModel } from '../../../../types';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRuleRunLogsCount, RulesKeys } from '../../../api/rules-engine-api';
import RulesEngineRunLogsGrid from './rules-engine-run-logs-grid';
import {
  isAbleToSeeUIElement,
  RulesEngineRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';

import RulesDetailsSummary from './rules-details-summary';
import StatusLabel from '../../../shared/status-label';
import { StatCardNew } from '../../../shared/stat-card-new';
import SharedCard from '../../../shared/shared-card';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Update from '@mui/icons-material/Update';
import Check from '@mui/icons-material/Check';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
interface RulesEngineDrawerDetailsProps {
  selectedRule: RuleModel;
  allPolicies: Policy[] | undefined;
  onEdit: (rule: RuleModel) => void;
  onEnable: (rule: RuleModel) => void;
  onDisable: (rule: RuleModel) => void;
  onDelete: (rule: RuleModel) => void;
  onClone: (rule: RuleModel) => void;
  onClose: () => void;
  rules: RuleModel[];
  executionCountsData?: {
    total: number | React.ReactNode;
    successful: number | React.ReactNode;
    failed: number | React.ReactNode;
  };
  openRulesDetailsDrawer: boolean;
  isLoading?: boolean;
}

export default function RulesEngineDrawerDetails({
  selectedRule,
  allPolicies,
  onEdit,
  onEnable,
  onDisable,
  onDelete,
  onClone,
  onClose,
  rules,
  executionCountsData,
  openRulesDetailsDrawer,
  isLoading = false,
}: RulesEngineDrawerDetailsProps) {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const { rulesEngineRoles } = useUserSettings();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const isEnabled = selectedRule.basicInfo.status === 'Enabled';

  const { data: countsData, isLoading: isLoadingLogs } = useQuery({
    queryKey: [RulesKeys.RunLogs, 'count', selectedRule.id],
    queryFn: () => getRuleRunLogsCount(selectedRule.id),
    enabled: !!selectedRule?.id && !executionCountsData,
  });

  const { executionCounts, lastTriggered } = useMemo(() => {
    return {
      executionCounts: executionCountsData ||
        countsData || {
          total: 0,
          successful: 0,
          failed: 0,
        },
      lastTriggered: countsData?.lastTriggered || null,
    };
  }, [countsData, executionCountsData]);

  const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp) return null;

    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  const canEditRules = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.EditRules.value
  );
  const canChangeRuleStatus = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.ChangeRuleStatus.value
  );
  const canCloneRules = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.AddRules.value
  );
  const canDeleteRules = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.DeleteRules.value
  );

  const editDisabledReason = canEditRules
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.EditRules.name);
  const changeRuleStatusDisabledReason = canChangeRuleStatus
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.ChangeRuleStatus.name);
  const cloneDisabledReason = canCloneRules
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.AddRules.name);
  const deleteDisabledReason = canDeleteRules
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.DeleteRules.name);
  const status = selectedRule.basicInfo.status;

  const actionsMenu: SimpleDrawerAction[] | undefined = selectedRule
    ? [
        {
          label: 'Edit',
          icon: <Edit fontSize="small" />,
          onClick: () => onEdit(selectedRule),
          disabled: !canEditRules,
          disabledReason: editDisabledReason,
        },
        ...(status === 'Disabled'
          ? [
              {
                label: 'Enable',
                icon: <CheckCircle fontSize="small" />,
                onClick: () => onEnable(selectedRule),
                disabled: !canChangeRuleStatus,
                disabledReason: changeRuleStatusDisabledReason,
              },
            ]
          : []),
        ...(status === 'Enabled'
          ? [
              {
                label: 'Disable',
                icon: <Cancel fontSize="small" />,
                onClick: () => onDisable(selectedRule),
                disabled: !canChangeRuleStatus,
                disabledReason: changeRuleStatusDisabledReason,
              },
            ]
          : []),
        {
          label: 'Clone',
          icon: <LibraryAdd fontSize="small" />,
          onClick: () => onClone(selectedRule),
          disabled: !canCloneRules,
          disabledReason: cloneDisabledReason,
        },
        {
          label: 'Delete',
          icon: <Delete fontSize="small" />,
          onClick: () => onDelete(selectedRule),
          disabled: !canDeleteRules,
          disabledReason: deleteDisabledReason,
        },
      ]
    : undefined;

  return (
    <SimpleDrawer
      title="Rule Details"
      open={openRulesDetailsDrawer}
      onClose={onClose}
      isLoading={isLoadingLogs || isLoading}
      actionsMenu={actionsMenu}
    >
      <SharedCard
        key={`${selectedRule.id.toString()}-main-card`}
        sx={{ mb: 1 }}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {selectedRule.basicInfo.ruleName || 'N/A'}
            </Typography>
            {selectedRule.basicInfo.status === 'Draft' ? (
              <StatusLabel status={'DRAFT'} />
            ) : (
              <StatusLabel status={isEnabled ? 'ENABLED' : 'DISABLED'} />
            )}
          </Stack>
          <Typography
            variant="body2"
            color={theme.palette.semantic.text.body}
            sx={{ mb: 2 }}
          >
            {selectedRule.basicInfo.description}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 1, alignItems: 'center' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: 'text.secondary' }}
            >
              <Update fontSize="small" />
              <Typography
                variant="body2"
                color={theme.palette.semantic.text.support}
              >
                Last triggered: {formatDate(lastTriggered) || 'N/A'}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ px: 3, color: theme.palette.semantic.text.support }}
            >
              •
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: 'text.secondary' }}
            >
              <Edit fontSize="small" />
              <Typography
                variant="body2"
                color={theme.palette.semantic.text.support}
              >
                Last modified:{' '}
                {formatDate(selectedRule.basicInfo.lastModifiedDate) || 'N/A'}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ sm: 1 }}
            sx={{ mb: 2, alignItems: 'center' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: 'text.secondary' }}
            >
              <Check fontSize="small" />
              <Typography
                variant="body2"
                color={theme.palette.semantic.text.support}
              >
                Created:{' '}
                {formatDate(selectedRule.basicInfo.createdDate) || 'N/A'}
              </Typography>
            </Stack>
          </Stack>

          {selectedRule.basicInfo.status !== 'Draft' && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mb: 1, width: '100%' }}
            >
              <StatCardNew
                color="cyan"
                label="Total Executions"
                value={
                  executionCountsData ? (
                    executionCounts.total
                  ) : isLoadingLogs ? (
                    <Skeleton width={40} />
                  ) : (
                    executionCounts.total
                  )
                }
                sx={{ width: '100%', minWidth: 149 }}
              />
              <StatCardNew
                color="teal"
                label="Successful"
                value={
                  executionCountsData ? (
                    executionCounts.successful
                  ) : isLoadingLogs ? (
                    <Skeleton width={40} />
                  ) : (
                    executionCounts.successful
                  )
                }
                sx={{ width: '100%', minWidth: 149 }}
              />
              <StatCardNew
                color="rose"
                label="Failed"
                value={
                  executionCountsData ? (
                    executionCounts.failed
                  ) : isLoadingLogs ? (
                    <Skeleton width={40} />
                  ) : (
                    executionCounts.failed
                  )
                }
                sx={{ width: '100%', minWidth: 149 }}
              />
            </Stack>
          )}
        </Box>
      </SharedCard>
      <SharedCard key={`${selectedRule.id.toString()}-tabs`}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="rule details tabs"
            >
              <Tab label="Overview" />
              <Tab label="Logs" />
            </Tabs>
          </Box>
          <Box sx={{ pt: 1, pb: 2 }}>
            {tabValue === 0 && (
              <RulesDetailsSummary selectedRule={selectedRule} />
            )}
            {tabValue === 1 && (
              <RulesEngineRunLogsGrid
                ruleId={selectedRule.id}
                policies={allPolicies || []}
                rules={rules}
              />
            )}
          </Box>
        </Box>
      </SharedCard>
    </SimpleDrawer>
  );
}
